/**
 * Pure duration / timespan helpers — no external dependencies.
 *
 * Cover the two duration shapes that flow across the EthisysCore wire: a
 * `"HH:mm:ss"` timespan string (.NET `TimeSpan`) and a millisecond count.
 */
import { MS_PER_DAY, MS_PER_HOUR, MS_PER_MINUTE } from "./constants";

/** Converts a `"HH:mm:ss"` timespan string to milliseconds. */
export const timespanToMilliseconds = (timespan: string): number => {
  if (!timespan) return 0;

  const [h = "0", m = "0", s = "0"] = timespan.split(":");
  const hours = parseInt(h || "0", 10);
  const minutes = parseInt(m || "0", 10);
  const seconds = parseInt(s || "0", 10);

  return (
    (isNaN(hours) ? 0 : hours) * 60 * 60 * 1000 +
    (isNaN(minutes) ? 0 : minutes) * 60 * 1000 +
    (isNaN(seconds) ? 0 : seconds) * 1000
  );
};

/** Converts milliseconds to hours (as a float). */
export const millisecondsToHours = (ms: number): number => ms / MS_PER_HOUR;

/** Converts hours to milliseconds. */
export const hoursToMilliseconds = (hours: number): number => hours * MS_PER_HOUR;

/**
 * The one human-readable duration formatter. Takes a MILLISECOND count and renders
 * the largest non-zero units as `Xd Xh Xm` (e.g. `4h 15m`, `2d 3h`, `45m`). Zero is
 * a real value (`0m`); a nullish/NaN/negative input is missing data and returns the
 * em-dash. Minute granularity — seconds are not shown.
 *
 * Feed non-millisecond inputs through the converters in this module:
 *   `formatDuration(timespanToMilliseconds("04:15:00"))`  // "HH:mm:ss" TimeSpan
 *   `formatDuration(hoursToMilliseconds(2.5))`            // fractional hours
 */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "—";
  if (ms < MS_PER_MINUTE) return "0m";

  const days = Math.floor(ms / MS_PER_DAY);
  const hours = Math.floor((ms % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((ms % MS_PER_HOUR) / MS_PER_MINUTE);

  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  return parts.join(" ");
};

/**
 * Trims an API-supplied `"HH:mm:ss"` down to the `"HH:mm"` string used by
 * MUI `TimePicker`-backed forms. Returns the supplied fallback when the value
 * is missing or shorter than five characters.
 */
export function toHHmm(value: string | null | undefined, fallback: string): string {
  if (!value || value.length < 5) return fallback;
  return value.slice(0, 5);
}

/**
 * Serialises a non-empty `"HH:mm"` / `"HH:mm:ss"` value back to the canonical
 * `"HH:mm:ss"` API format. Returns `null` for empty input so callers can block
 * the save rather than silently persisting midnight — an empty TimePicker
 * (cleared via keyboard) is an unsaved edit, not a legitimate `00:00:00` value.
 */
export function toHHmmss(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.length === 5 ? `${trimmed}:00` : trimmed;
}
